import { ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { FunctionComponent, useContext, useEffect } from 'react';
import { inject } from 'mobx-react';

import { AdvertisingStore, TargetingEvent } from 'bernie-plugin-ads-loader-script';

import { evaluateAndLogExperiment } from 'src/common/stores/utils';
import { SearchCriteriaContext } from 'stores/contexts';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

interface MesoScriptsProps {
  advertising?: AdvertisingStore;
  page?: PageStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
}

const ADS_HOLD_OUT_TEST_NAME = 'MeSo_HSR_Hold_Out_AB_Test';
const ADS_HOLD_OUT_FEATURE_GATE_NAME = 'MeSo_HSR_Holdout_Feature_Gate';

const initializeMesoExperiments = (context: ContextStore, experiment: ExperimentStore, page: PageStore) => {
  window.meso = window.meso || {};
  window.meso.experiments = window.meso.experiments || [];
  const featureGateBucket = evaluateAndLogExperiment(ADS_HOLD_OUT_FEATURE_GATE_NAME, context, experiment, page);
  const experimentBucket = featureGateBucket ? evaluateAndLogExperiment(ADS_HOLD_OUT_TEST_NAME, context, experiment, page) : 0;

  window.meso.experiments.push({
    type: 'update',
    experiments: {
      // eslint-disable-next-line camelcase
      MeSo_HSR_Holdout_Feature_Gate: featureGateBucket || 0,
      // eslint-disable-next-line camelcase
      MeSo_HSR_Hold_Out_AB_Test: experimentBucket || 0,
    },
  });
};

const buildTargetingEvent = (searchCriteria: SearchCriteriaModel): TargetingEvent => {
  const { regionId, startDate, endDate, adults, children, star, amenities, lodging, origin } = searchCriteria;

  const targetingEvent: TargetingEvent = {
    type: 'update',
    targeting: {
      dest: regionId,
      dateEnd: endDate,
      dateStart: startDate,
      adults,
      kids: children,
      locResolver: 'gaia',
      origin,
      ha: amenities?.toString() ?? '',
      hpt: lodging?.toString() ?? '',
      sr: star?.toString() ?? '',
    },
  };

  return targetingEvent;
};

const processTargeting = (searchCriteria: SearchCriteriaModel) => {
  const targetingEvent = buildTargetingEvent(searchCriteria);

  window.meso = window.meso || {};
  window.meso.targeting = window.meso.targeting || [];
  window.meso.targeting.push(targetingEvent);
};

export const MesoScripts: FunctionComponent<MesoScriptsProps> = inject(
  'page',
  'experiment',
  'context'
)(({ page, experiment, context }) => {
  const searchCriteria: SearchCriteriaModel = useContext(SearchCriteriaContext);
  // Do not perform this on the server side where window does not exist.
  if (typeof window !== 'undefined') {
    useEffect(() => {
      initializeMesoExperiments(context, experiment, page);
    }, [context, experiment, page]);

    useEffect(() => {
      if (searchCriteria) {
        processTargeting(searchCriteria);
      }
    }, [searchCriteria]);
  }

  // This component does not render anything to the DOM.
  return null;
});
